import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { clearQuantityFlag, clearStepFlag } from '../lib/recipeFlags';
import { loadState, saveState } from '../lib/persist.js';
import { EMPTY_TRIP, clearTrip, completeTrip, reopenTrip, startTrip } from '../lib/pickupTrip.js';

const OrderContext = createContext(null);

const STORAGE_KEY = 'order';

// The selected recipe lives here rather than in any one screen so Home and
// Recipe always show the same meal.
//
// Persisted since Stage 5, because the meal is the thing most likely to be
// interrupted: this is the state someone leaves behind when they lock the
// phone in the shop. Everything below is saved EXCEPT `playback` — see
// setPlayback for why an object URL must never outlive its tab.
const EMPTY = {
  input: '',
  recipe: null,
  // How this recipe got picked — cooked before, saved, or found online. Derived
  // per search and carried for display only; it is never stored on the recipe.
  match: null,
  // Working copies of the parsed recipe. The user edits these; `recipe` stays
  // as parsed so we can always tell what was suggested vs. what they changed.
  servings: null,
  ingredients: [],
  steps: [],
  // The shop the user is going to, and the (at most three) real branches we
  // found near them. `store` is one of `storeOptions` — never a shop of our
  // own invention, and never one we have claimed does pickup.
  store: null,
  storeOptions: [],
  // The shopping trip, and the only record this release keeps of it: that the
  // user opened a store's website, and that they later said they'd picked the
  // food up. Nothing here is inferred — see lib/pickupTrip.
  trip: EMPTY_TRIP,
  // A video the user picked off their phone, held only so they can watch it
  // while writing the recipe out. See setPlayback below for why it is not part
  // of the recipe.
  playback: null,
};

function keyFor(index, name) {
  return `${index}-${name}`;
}

export function OrderProvider({ children }) {
  // A restored order is merged onto EMPTY rather than used as-is, so a record
  // written before a field existed comes back with that field's default
  // instead of `undefined` reaching a screen.
  const [state, setState] = useState(() => {
    const stored = loadState(STORAGE_KEY);
    return stored ? { ...EMPTY, ...stored, playback: null } : EMPTY;
  });

  // `playback` is stripped on the way out, not just ignored on the way in. It
  // is a `URL.createObjectURL` handle into a page that no longer exists by the
  // time this is read back — writing it down would persist an address that is
  // guaranteed to be dead, and the recipe screen would render a broken player
  // for a video the user cannot see and we never read.
  useEffect(() => {
    const { playback, ...persistable } = state;
    saveState(STORAGE_KEY, persistable);
  }, [state]);

  // Object URLs are handles into this page's memory, not addresses. They have
  // to be released by hand, and they die with the tab — which is exactly why
  // one must never be written into a recipe as `media.videoUrl`. A saved
  // recipe reopened tomorrow would point at nothing. The ref tracks the live
  // handle so it can be revoked no matter what replaced it.
  const playbackUrlRef = useRef(null);

  function releasePlayback() {
    if (playbackUrlRef.current) {
      URL.revokeObjectURL(playbackUrlRef.current);
      playbackUrlRef.current = null;
    }
  }

  useEffect(() => releasePlayback, []);

  const value = useMemo(
    () => ({
      ...state,
      hasRecipe: Boolean(state.recipe),

      update: (patch) => setState((prev) => ({ ...prev, ...patch })),

      reset: () => {
        releasePlayback();
        // The shops near you do not change because you picked a different
        // meal, so the store survives a reset with the list it came from. The
        // trip does not: it was a trip for the old meal, and carrying it over
        // would show a pickup the user completed for something else.
        setState((prev) => ({ ...EMPTY, store: prev.store, storeOptions: prev.storeOptions }));
      },

      // Ingredients the user actually intends to buy. Excluding something is a
      // shopping decision, so it changes this list and nothing else.
      shoppingList: state.ingredients.filter((item) => item.included),

      // Adopt a recipe as the selected one, seeding the editable working copies
      // from it. `match` says how it was found; it is display-only.
      selectRecipe: (recipe, input, match = null) =>
        setState((prev) => ({
          ...prev,
          input,
          recipe,
          match,
          // A different meal is a different shop, even at the same store.
          trip: clearTrip(),
          // Null means the source never said. The screen offers a starting
          // point to adjust rather than printing a number we made up.
          servings: recipe.servings ?? 2,
          ingredients: recipe.ingredients.map((ingredient, index) => ({
            ...ingredient,
            id: keyFor(index, ingredient.name),
            // Every ingredient starts on the shopping list. We never assume the
            // user already owns the oil or the salt.
            included: true,
            fromRecipe: true,
            quantityMissing: Boolean(ingredient.quantityMissing),
          })),
          // Steps become a working copy for the same reason ingredients do: an
          // unclear one has to be fixable, and fixing it has to clear the flag.
          steps: recipe.steps.map((text, index) => ({
            id: `step-${index}`,
            text,
            unclear: (recipe.unclearSteps ?? []).includes(index),
          })),
        })),

      setServings: (servings) =>
        setState((prev) => ({ ...prev, servings: Math.max(1, Math.min(12, servings)) })),

      addIngredient: () =>
        setState((prev) => ({
          ...prev,
          ingredients: [
            ...prev.ingredients,
            {
              id: keyFor(prev.ingredients.length, `new-${Date.now()}`),
              name: '',
              quantity: '',
              included: true,
              fromRecipe: false,
              quantityMissing: false,
            },
          ],
        })),

      editIngredient: (id, patch) =>
        setState((prev) => ({
          ...prev,
          ingredients: prev.ingredients.map((item) => {
            if (item.id !== id) return item;
            const next = { ...item, ...patch };
            // Typing in the amount field is the user answering the question we
            // flagged, so the flag comes off.
            return 'quantity' in patch ? clearQuantityFlag(next) : next;
          }),
        })),

      editStep: (id, text) =>
        setState((prev) => ({
          ...prev,
          steps: prev.steps.map((step) => (step.id === id ? clearStepFlag({ ...step, text }) : step)),
        })),

      // Taking something off the shopping list is not the same as deleting it
      // from the recipe. An ingredient the recipe calls for stays visible and
      // restorable — the recipe still needs it, the user just isn't buying it.
      // Rows the user added themselves have no recipe behind them, so removing
      // one really does delete it.
      removeIngredient: (id) =>
        setState((prev) => ({
          ...prev,
          ingredients: prev.ingredients.flatMap((item) => {
            if (item.id !== id) return [item];
            return item.fromRecipe ? [{ ...item, included: false }] : [];
          }),
        })),

      restoreIngredient: (id) =>
        setState((prev) => ({
          ...prev,
          ingredients: prev.ingredients.map((item) =>
            item.id === id ? { ...item, included: true } : item
          ),
        })),

      // A picked video is something to watch, not something we understood.
      // It is kept beside the recipe rather than on it, so nothing downstream
      // can mistake "there is a video here" for "this recipe came from video".
      setPlayback: (file) => {
        releasePlayback();
        if (!file) {
          setState((prev) => ({ ...prev, playback: null }));
          return;
        }
        const url = URL.createObjectURL(file);
        playbackUrlRef.current = url;
        setState((prev) => ({ ...prev, playback: { url, name: file.name } }));
      },

      clearPlayback: () => {
        releasePlayback();
        setState((prev) => ({ ...prev, playback: null }));
      },

      // Called when the user opens the store's website. It records that they
      // went, and that is all it is allowed to mean — we cannot see what
      // happens in that tab, and this must never be mistaken for an order.
      beginPickup: (store) => setState((prev) => ({ ...prev, trip: startTrip(prev.trip, store ?? prev.store) })),

      // The user telling us, in as many words. There is no other route to this
      // state: no timer, no return-to-tab detection, no guess.
      markPickedUp: () => setState((prev) => ({ ...prev, trip: completeTrip(prev.trip) })),

      // Pressed by mistake, or the trip didn't happen after all.
      undoPickedUp: () => setState((prev) => ({ ...prev, trip: reopenTrip(prev.trip) })),
    }),
    [state]
  );

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
}

export function useOrder() {
  const context = useContext(OrderContext);
  if (!context) throw new Error('useOrder must be used inside <OrderProvider>');
  return context;
}
